// The Conversation tab's Translate button, as one hook.
//
// The round trip is: POST (which only QUEUES the work — a model call can't run on
// the web cycle) → a `conversation.translation.ready` POINTER on the employee's
// own private channel → a REST read of the payload it names. Pusher carries the
// trigger and REST carries the data, because a translated transcript is tens of
// kilobytes against a per-event cap of a few.
//
// Two Pusher configs end up live on this page at once, and that is deliberate:
// the transcript spectates the CLIENT's channel through `adminPusherConfig`,
// while this reply lands on `private-debug-<email>` through `employeePusherConfig`.
// The two auth routes authorize different channel families and must not be merged
// (see lib/pusher.ts). No `usePusherHold` here: `employeePusherConfig` already sets
// `idleDisconnectMs: 0`, so its socket survives a backgrounded tab outright,
// which is the same reason the debug and insights chats do not declare a hold
// either — an operator presses Translate and looks away.
//
// The translation is keyed by the CONTENT digest of what was translated, never by
// the project. That is what keeps a live conversation honest: messages that
// arrive after the press are not in the overlay and render in their original
// language, and `staleBy` says how many, rather than the overlay quietly
// pretending to cover them.
import { usePusherChannel } from "@recruiter/shared";
import type {
  ConversationTranslation,
  ConversationTranslationReady,
} from "@recruiter/shared/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError, postConversationTranslate } from "./api";
import {
  buildOverlay,
  translationShortfall,
  translationStatus,
  type TranslationOverlay,
  type TranslationStatus,
} from "./conversation-translation";
import { debugChannelFor, employeePusherConfig } from "./pusher";
import { adminKeys, conversationTranslationQuery, meQuery } from "./queries";

/** How long to wait for the ready event before letting the operator try again.
 *
 *  The reply is a ONE-SHOT event with no REST reconcile behind it, so anything
 *  that eats it — a socket that dropped, a job that died past its retries, a
 *  subscription that was not live yet when a cache hit published within
 *  milliseconds of the POST — would otherwise leave the button disabled and
 *  "Translating" forever, with a page reload as the only way out. Generous
 *  against the work itself (the job runs on the slow lane and a long transcript
 *  spends up to ten concurrent model calls), so this fires on a LOST reply
 *  rather than on a slow one, and a late reply still lands and clears it. */
const REPLY_DEADLINE_MS = 120_000;

export interface ConversationTranslationState {
  status: TranslationStatus;
  /** A translation is in flight. True DURING a re-translate too, while the
   *  previous one is still on screen. */
  pending: boolean;
  /** The id → English map, or null whenever the original is what should render. */
  overlay: TranslationOverlay | null;
  /** True while the translation is the one on screen. */
  showing: boolean;
  /** The dominant source language the model reported, in English. */
  sourceLanguage: string;
  /** What came back in the original language anyway, or null when nothing did. */
  shortfall: string | null;
  /** Messages that arrived after this translation was asked for. */
  staleBy: number;
  error: string | null;
  /** Whether there is a channel to be answered on at all: false until
   *  `GET /api/admin/me` has resolved the employee's email, since the channel is
   *  derived from it. Pressing before then would enqueue a job whose one-shot
   *  reply is published to a channel this tab never named, and Pusher replays
   *  nothing to a late subscriber, so the operator would pay for a translation
   *  and watch the deadline run out.
   *
   *  It does NOT promise the SUBSCRIBE has completed — `usePusherChannel` exposes
   *  no such flag — so a press in the few milliseconds between naming the channel
   *  and joining it can still lose a cache-hit reply. That residual race is what
   *  `REPLY_DEADLINE_MS` is for, and the retry after it is free: the digest is
   *  already cached, so the second press answers immediately. */
  canAsk: boolean;
  translate: () => void;
  toggle: () => void;
}

export function useConversationTranslation(
  projectId: string,
  messageCount: number,
): ConversationTranslationState {
  const me = useQuery(meQuery);
  const email = me.data?.email ?? null;
  const queryClient = useQueryClient();

  const [key, setKey] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [showing, setShowing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // How long the transcript was when each translated KEY was asked for, so the bar
  // can say what the overlay on screen does not cover.
  //
  // Kept per key in a ref and read during RENDER rather than committed by an
  // effect: an effect necessarily runs after the paint that first shows the new
  // payload, so for one frame `data` was the fresh translation while the baseline
  // was still the old one (0 on the first press) and the bar shouted "20 messages
  // arrived after this was translated" about a translation covering the whole
  // transcript. Keyed rather than single-valued so it stays attached to the
  // reading being displayed, not to the latest press.
  const pressedAtCountRef = useRef(0);
  const baselineByKeyRef = useRef<Map<string, number>>(new Map());
  // Only the LATEST press is answered: pressing twice must not let the first
  // reply overwrite the second's, and an event for any other request (this
  // channel carries every one of the employee's tabs, and both floating chats)
  // is not ours.
  // The LAST translation that actually loaded, held across a re-key. Without it
  // the "a translation on screen wins" rule breaks exactly where it matters most:
  // pressing Translate again on a grown transcript mints a NEW key, the query for
  // that key starts empty, and the overlay plus the Show original control would
  // vanish for the length of a REST fetch — the trap `translationStatus` exists to
  // prevent, sprung by the retry that was supposed to help. Declared up here with
  // the rest of the state because the 404 path below CLEARS it.
  const [lastLoaded, setLastLoaded] = useState<ConversationTranslation | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);
  const resolvedRef = useRef<Set<string>>(new Set());

  const channel = email ? debugChannelFor(email) : null;
  const events = usePusherChannel<ConversationTranslationReady>(
    channel,
    ["conversation.translation.ready"],
    employeePusherConfig,
  );

  // Matched on BOTH sides rather than by scanning only what is new: a cache hit
  // publishes the pointer in milliseconds, so the event can land before the POST
  // that asked for it has even returned its id. Waking on either dependency means
  // the two can arrive in either order.
  //
  // A FAILURE is not the end of the request. The worker publishes `ok=false` and
  // then re-raises, so the queue retries the same job under the same request id
  // and a later attempt can still succeed. Only a success is terminal, and the
  // LAST matching event wins — otherwise the first failure would be latched
  // forever and the console would sit on "Translation failed" while the finished
  // translation sat in the cache.
  useEffect(() => {
    if (!requestId || resolvedRef.current.has(requestId)) return;
    const matches = events.filter((candidate) => candidate?.request_id === requestId);
    const event = matches[matches.length - 1];
    if (!event) return;
    if (event.ok && event.key) {
      setPending(false);
      resolvedRef.current.add(requestId);
      // Refetch even when the key is UNCHANGED, which is the case a retry over a
      // partial result always lands in: the transcript did not change, so the
      // digest did not either, but the worker refuses to reuse a degraded entry
      // (`is_reusable`) and has just overwritten it with a better one. Without
      // this the query is already holding data for that key at `staleTime:
      // Infinity`, `setKey` sets an identical value, nothing refetches, and the
      // operator watches a fresh set of model calls change nothing on screen.
      void queryClient.invalidateQueries({
        queryKey: adminKeys.conversationTranslation(projectId, event.key),
      });
      // Recorded AGAINST THIS KEY rather than as "the" baseline. Until the REST
      // fetch for it lands, the overlay on screen is still the previous
      // translation, and a single baseline moved here would collapse `staleBy` to
      // 0 while the genuinely stale reading it describes is what the operator is
      // looking at. Keyed, `staleBy` reads the entry for whichever translation is
      // actually displayed, so writing it now is safe and needs no second step.
      baselineByKeyRef.current.set(event.key, pressedAtCountRef.current);
      setKey(event.key);
      setShowing(true);
      setError(null);
    } else {
      // `pending` follows the SERVER's answer about whether another attempt is
      // coming. Where the handler re-raises, the queue redelivers the same job
      // under this same request id, so holding the button is right: releasing it
      // invites a second press that enqueues a second translation racing that
      // retry. Where the handler RETURNS (nothing translatable, a translation that
      // came back empty), this event is the last word — holding the button then
      // just makes the operator watch a spinner for the whole deadline on a job
      // that already finished, after a message telling them to try again.
      setPending(event.retrying);
      setError(
        event.error ??
          (event.retrying ? "The translation failed. Still retrying." : "The translation failed."),
      );
    }
  }, [events, requestId, projectId, queryClient]);

  const translation = useQuery({
    ...conversationTranslationQuery(projectId, key ?? ""),
    enabled: key !== null,
  });

  // A key is a cache entry, not a record: it can age out between the ready event
  // and the read, and a 404 here means "ask again", not "something is broken".
  useEffect(() => {
    const failure = translation.error;
    if (!failure) return;
    const gone = failure instanceof ApiError && failure.status === 404;
    // Drop the cached ENTRY, not just our copy of its message. React Query keeps
    // the error on that key, and the natural retry re-mints the SAME digest (the
    // transcript has not changed) — so `setKey(K)` would remount a query still
    // carrying this 404, this effect would fire again on the stale error, and the
    // `setKey(null)` below would cancel the very refetch about to deliver the new
    // payload. The operator would sit on "expired" with a valid translation in
    // the cache, and pressing again would do it all over.
    if (key !== null) {
      queryClient.removeQueries({
        queryKey: adminKeys.conversationTranslation(projectId, key),
      });
    }
    setError(
      gone
        ? "That translation expired. Translate the conversation again."
        : "Couldn't load the translation.",
    );
    if (gone) {
      // Drop back to the original words, which is what that message CLAIMS
      // happened. `lastLoaded` otherwise keeps the previous overlay rendering
      // underneath a banner saying it expired — the console contradicting itself
      // on the one surface whose whole job is showing what was really said. Only
      // on a 404: a transient read failure is no reason to throw away a good
      // translation the operator is reading.
      setLastLoaded(null);
      setShowing(true);
    }
    setKey(null);
  }, [translation.error, key, projectId, queryClient]);

  const translate = useCallback(() => {
    if (!channel) {
      // Belt to the button's braces: without a live subscription the reply has
      // nowhere to land, and a job enqueued here is spend with no result.
      setError("Still connecting. Try again in a moment.");
      return;
    }
    setError(null);
    setPending(true);
    // Stop answering for the PREVIOUS request before this one has an id. It is
    // still named here otherwise, and it is not in `resolvedRef` if it failed, so
    // any event arriving on this shared channel while the POST is in flight would
    // re-run the match effect, find that old failure, and clear `pending` plus
    // paint its error over the press now in flight.
    setRequestId(null);
    pressedAtCountRef.current = messageCount;
    postConversationTranslate(projectId)
      .then((ack) => {
        // The ack is a wire DTO (camelCase); the Pusher event beside it is a raw
        // event model (snake_case). Two conventions, one round trip.
        //
        // We do not SUBSCRIBE to `ack.channel` — the subscription has to be live
        // before the POST, because a cache hit publishes within milliseconds and
        // Pusher replays nothing to a channel joined afterwards. So the field
        // does the job it was added for from the other side: it CHECKS the
        // channel this hook derived. `debugChannelFor` mirrors the backend's
        // `channel_for_debug`, and a mismatch between the two would otherwise
        // mean subscribing to a channel nothing publishes to and waiting out the
        // deadline with no idea why.
        if (channel && ack.channel !== channel) {
          setPending(false);
          setError("This console is out of step with the server. Reload and try again.");
          console.error(
            `[translate] channel drift: subscribed ${channel}, server publishes ${ack.channel}`,
          );
          return;
        }
        setRequestId(ack.requestId);
      })
      .catch((failure: unknown) => {
        setPending(false);
        setRequestId(null);
        setError(
          failure instanceof ApiError
            ? (failure.detail ?? "Couldn't ask for a translation.")
            : "Couldn't ask for a translation.",
        );
      });
  }, [projectId, messageCount, channel]);

  // Armed on `pending` alone, so it also covers a POST that never settles (there
  // is no request id yet in that window). Cleared the moment pending drops.
  useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(() => {
      setPending(false);
      setError("No answer came back for that translation. Try again.");
    }, REPLY_DEADLINE_MS);
    return () => clearTimeout(timer);
  }, [pending]);

  const toggle = useCallback(() => setShowing((current) => !current), []);

  const fetched = translation.data ?? null;
  useEffect(() => {
    if (!fetched) return;
    setLastLoaded(fetched);
  }, [fetched]);
  const data = fetched ?? lastLoaded;

  const overlay = useMemo(() => (data ? buildOverlay(data) : null), [data]);

  const status = translationStatus({
    hasData: data !== null,
    pending: pending || (key !== null && translation.isPending),
    error: error !== null,
  });

  return {
    status,
    pending,
    overlay: showing ? overlay : null,
    showing,
    sourceLanguage: data?.sourceLanguage ?? "",
    shortfall: data ? translationShortfall(data) : null,
    // Read off the reading ACTUALLY on screen, so it is right on the very first
    // frame that shows it.
    staleBy: data
      ? Math.max(0, messageCount - (baselineByKeyRef.current.get(data.key) ?? messageCount))
      : 0,
    error,
    canAsk: channel !== null,
    translate,
    toggle,
  };
}
